import React, { useMemo } from "react";

const getSteps = (isEnglish) => {
  if (isEnglish) {
    return [
      { key: "initializing", label: "Preparing analysis" },
      { key: "fetching", label: "Fetching transfers" },
      { key: "classifying", label: "Classifying transactions" },
      { key: "pricing", label: "Building price index" },
      { key: "fifo", label: "Running FIFO" },
      { key: "finalizing", label: "Finalizing analysis" },
      { key: "transferring_results", label: "Transferring results" },
      { key: "done", label: "Ready" },
    ];
  }

  return [
    { key: "initializing", label: "Analyse vorbereiten" },
    { key: "fetching", label: "Transfers abrufen" },
    { key: "classifying", label: "Transaktionen klassifizieren" },
    { key: "pricing", label: "Preisindex berechnen" },
    { key: "fifo", label: "FIFO ausführen" },
    { key: "finalizing", label: "Analyse finalisieren" },
    { key: "transferring_results", label: "Ergebnisse werden übertragen" },
    { key: "done", label: "Fertig" },
  ];
};

function CheckIcon() {
  return (
    <svg
      className="h-3.5 w-3.5"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 01.006 1.414l-7.5 7.57a1 1 0 01-1.42.003L3.3 9.78a1 1 0 011.4-1.43l3.09 3.03 6.79-6.86a1 1 0 011.414-.23z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function StepIcon({ state }) {
  if (state === "done") {
    return (
      <span className="flex h-7 w-7 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600">
        <CheckIcon />
      </span>
    );
  }

  if (state === "active") {
    return (
      <span className="relative flex h-7 w-7 items-center justify-center rounded-full border border-indigo-200 bg-white text-indigo-600 shadow-[0_0_0_4px_rgba(99,102,241,0.08)]">
        <span className="h-2.5 w-2.5 rounded-full bg-indigo-600" />
      </span>
    );
  }

  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-slate-50">
      <span className="h-2 w-2 rounded-full bg-slate-300" />
    </span>
  );
}

export default function VeWorldLoadingOverlay({
  phase = "loading",
  progress = 0,
  currentStep = "initializing",
  completedSteps = [],
  progressMessage = "",
  progressDetail = "",
  indeterminate = false,
  onRetry,
  onBack,
  onSupport,
  language = "de",
  errorTitle = "",
  errorMessage = "",
  errorDetail = "",
  errorCode = "",
  datasetId = null,
  taxYear = null,
  /** Nur setzen, wenn bewusst ein Bild statt des VTHO-Blitz-SVGs gewünscht ist (Standard: Blitz). */
  logoSrc = null,
  logoAlt = "",
}) {
  const isEnglish = String(language || "").toLowerCase().startsWith("en");
  const allSteps = getSteps(isEnglish);
  const steps = useMemo(
    () => allSteps.filter((step) => step.key !== "done"),
    [allSteps]
  );

  const isError = phase === "error";
  const displayPct =
    currentStep === "done"
      ? 100
      : Math.min(100, Math.max(0, Math.round(Number(progress) || 0)));
  const isComplete = displayPct >= 100;

  const completedSet = useMemo(
    () => new Set(Array.isArray(completedSteps) ? completedSteps : []),
    [completedSteps]
  );

  const activeIndex = useMemo(() => {
    if (currentStep === "done") return steps.length - 1;
    const idx = steps.findIndex((s) => s.key === currentStep);
    return idx >= 0 ? idx : 0;
  }, [steps, currentStep]);

  const currentLabel =
    currentStep === "done"
      ? isEnglish
        ? "Ready"
        : "Fertig"
      : steps[activeIndex]?.label || steps[0]?.label || "";

  const completedCount = useMemo(() => {
    if (currentStep === "done") return steps.length;
    return steps.filter((s) => completedSet.has(s.key)).length;
  }, [steps, completedSet, currentStep]);

  const title =
    phase === "error"
      ? errorTitle ||
        (isEnglish
          ? "Analysis could not be completed"
          : "Analyse konnte nicht abgeschlossen werden")
      : isEnglish
        ? "Analysis in progress"
        : "Analyse läuft";

  const subtitle =
    phase === "error"
      ? errorMessage ||
        (isEnglish
          ? "The calculation was stopped before a reliable tax result could be created. Your data was not changed. Please try again."
          : "Die Berechnung wurde gestoppt, bevor ein verlässliches Steuerergebnis erstellt werden konnte. Deine Daten wurden nicht verändert. Bitte versuche es erneut.")
      : progressMessage ||
        (isEnglish
          ? "Your blockchain data is being processed and reconciled."
          : "Deine Blockchain-Daten werden verarbeitet und aufbereitet.");

  const stepMetaLabel = isEnglish ? "Current step" : "Aktueller Schritt";
  const progressLabel = isEnglish ? "Progress" : "Fortschritt";
  const pipelineLabel = isEnglish ? "Pipeline" : "Pipeline";
  const secureLabel = isEnglish ? "Secure processing" : "Sichere Verarbeitung";
  const retryLabel = isEnglish ? "Try again" : "Erneut versuchen";
  const loadingLabel = isEnglish ? "Loading…" : "Lädt…";
  const liveLabel = isEnglish ? "Live" : "Live";
  const completedLabel = isEnglish ? "Completed" : "Abgeschlossen";
  const currentLabelSmall = isEnglish ? "Current" : "Aktiv";
  const queuedLabel = isEnglish ? "Queued" : "Wartet";
  const runningLabel = isEnglish ? "Running" : "In Arbeit";
  const doneLabel = isEnglish ? "Done" : "Fertig";

  return (
    <div className="fixed inset-0 z-[100] animate-overlay-fade-in" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-950/86 backdrop-blur-[6px]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.30),rgba(2,6,23,0.50))]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(15,23,42,0)_0%,rgba(15,23,42,0.14)_46%,rgba(2,6,23,0.56)_100%)]" />
      <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_220px_rgba(2,6,23,0.60)]" />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative z-10 w-full max-w-[580px] overflow-hidden rounded-[28px] border border-white/85 bg-white/[0.985] shadow-[0_36px_100px_rgba(2,6,23,0.40)] ring-1 ring-black/[0.03] animate-overlay-in will-change-[transform,opacity]">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(255,255,255,0.97))]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
          <div className="pointer-events-none absolute -top-20 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-slate-100/40 blur-3xl" />

          <div className="relative z-10 px-5 py-5 sm:px-6 sm:py-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3.5">
                <div className="relative grid h-[52px] w-[52px] flex-shrink-0 place-items-center overflow-hidden rounded-[18px] border border-slate-200/85 bg-[linear-gradient(135deg,#2563eb_0%,#4f46e5_55%,#7c3aed_100%)] shadow-[0_14px_30px_rgba(79,70,229,0.20)]">
                  {logoSrc ? (
                    <img
                      src={logoSrc}
                      alt={logoAlt}
                      className="relative z-10 h-7 w-7 object-contain"
                      draggable={false}
                    />
                  ) : (
                    <svg
                      className="relative z-10 h-[22px] w-[22px] text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  )}
                  <div className="pointer-events-none absolute inset-0 opacity-70 overlay-logo-sheen" />
                </div>

                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                    V3Tax
                  </div>
                  <h2 className="truncate text-[1.08rem] font-semibold tracking-tight text-slate-900 sm:text-[1.14rem]">
                    {title}
                  </h2>
                </div>
              </div>

              <div
                className={[
                  "inline-flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-medium shadow-sm",
                  isError
                    ? "border border-rose-200/90 bg-rose-50/90 text-rose-700"
                    : "border border-slate-200/80 bg-white/90 text-slate-500",
                ].join(" ")}
              >
                <span
                  className={[
                    "h-1.5 w-1.5 rounded-full",
                    isError ? "bg-rose-500" : "bg-emerald-500",
                  ].join(" ")}
                />
                <span>{isError ? retryLabel : secureLabel}</span>
              </div>
            </div>

            <div className="mt-5 rounded-[24px] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.92),rgba(255,255,255,0.96))] p-4 shadow-[0_8px_24px_rgba(15,23,42,0.05)] sm:p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                    {stepMetaLabel}
                  </div>
                  <div className="mt-1 truncate text-[15px] font-semibold text-slate-900 sm:text-base">
                    {isError ? title : currentLabel}
                  </div>
                </div>

                <div className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 tabular-nums shadow-sm">
                  {indeterminate ? loadingLabel : `${displayPct}%`}
                </div>
              </div>

              <p className="mt-3 text-sm leading-6 text-slate-600">
                {subtitle}
              </p>

              {!isError && progressDetail ? (
                <div className="mt-2 truncate text-xs text-slate-500">
                  {progressDetail}
                </div>
              ) : null}

              {!isError && (
                <>
                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      <span>{progressLabel}</span>
                      <span>{pipelineLabel}</span>
                    </div>

                    <div className="relative h-2.5 overflow-hidden rounded-full bg-slate-200/80 ring-1 ring-inset ring-slate-200">
                      {indeterminate ? (
                        <div className="absolute inset-y-0 left-0 w-[34%] rounded-full bg-[linear-gradient(90deg,#2563eb_0%,#4f46e5_55%,#7c3aed_100%)] overlay-indeterminate" />
                      ) : (
                        <div
                          className={[
                            "absolute inset-y-0 left-0 rounded-full bg-[linear-gradient(90deg,#2563eb_0%,#4f46e5_55%,#7c3aed_100%)] transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                            isComplete ? "right-0" : "",
                          ].join(" ")}
                          style={isComplete ? undefined : { width: `${displayPct}%` }}
                        >
                          <div className="pointer-events-none absolute inset-0 overlay-progress-sheen" />
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
                      <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">
                        <span className="font-medium">{completedLabel}</span>
                        <span className="font-semibold tabular-nums text-slate-900">
                          {completedCount}/{steps.length}
                        </span>
                      </div>

                      <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1.5 text-indigo-700">
                        <span className="font-medium">{currentLabelSmall}</span>
                        <span className="font-semibold tabular-nums">
                          {Math.min(activeIndex + 1, steps.length)}/{steps.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {!isError && (
              <div
                className="mt-4 overflow-hidden rounded-[24px] border border-slate-200/80 bg-white shadow-[0_8px_24px_rgba(15,23,42,0.045)]"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                  <span>{pipelineLabel}</span>
                  <span>{liveLabel}</span>
                </div>

                <div className="p-2">
                  {steps.map((s, idx) => {
                    const done = currentStep === "done" || completedSet.has(s.key);
                    const active = !done && s.key === currentStep;
                    const state = done ? "done" : active ? "active" : "idle";

                    return (
                      <div
                        key={s.key}
                        className={[
                          "flex items-center gap-3 rounded-[18px] px-3 py-3 transition-colors",
                          active ? "bg-indigo-50/45" : "bg-white",
                        ].join(" ")}
                      >
                        <StepIcon state={state} />

                        <div className="min-w-0 flex-1">
                          <div
                            className={[
                              "truncate text-sm font-medium",
                              done
                                ? "text-slate-700"
                                : active
                                  ? "text-slate-900"
                                  : "text-slate-500",
                            ].join(" ")}
                          >
                            {s.label}
                          </div>

                          <div
                            className={[
                              "mt-0.5 text-[11px] font-medium",
                              done
                                ? "text-emerald-600"
                                : active
                                  ? "text-indigo-600"
                                  : "text-slate-400",
                            ].join(" ")}
                          >
                            {done ? doneLabel : active ? runningLabel : queuedLabel}
                          </div>
                        </div>

                        <div
                          className={[
                            "rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums",
                            done
                              ? "bg-emerald-50 text-emerald-700"
                              : active
                                ? "bg-indigo-50 text-indigo-700"
                                : "bg-slate-50 text-slate-500",
                          ].join(" ")}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {isError && (
              <div className="mt-4 space-y-4">
                <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm leading-6 text-rose-700">
                  {subtitle}
                </div>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={onRetry}
                    className="inline-flex items-center justify-center rounded-[18px] border border-transparent bg-[linear-gradient(135deg,#2563eb_0%,#4f46e5_55%,#7c3aed_100%)] px-4 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(79,70,229,0.24)] transition hover:translate-y-[-1px] hover:shadow-[0_16px_36px_rgba(79,70,229,0.28)] focus:outline-none focus:ring-4 focus:ring-indigo-100"
                  >
                    {retryLabel}
                  </button>

                  {onBack ? (
                    <button
                      type="button"
                      onClick={onBack}
                      className="inline-flex items-center justify-center rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      {isEnglish ? "Back to overview" : "Zur Übersicht"}
                    </button>
                  ) : null}

                  {onSupport ? (
                    <button
                      type="button"
                      onClick={onSupport}
                      className="inline-flex items-center justify-center rounded-[18px] border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      {isEnglish ? "Contact support" : "Support kontaktieren"}
                    </button>
                  ) : null}
                </div>

                {(errorDetail || errorCode || datasetId || taxYear) ? (
                  <details className="rounded-[20px] border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
                    <summary className="cursor-pointer select-none font-semibold text-slate-700">
                      {isEnglish ? "Technical reference" : "Technische Referenz"}
                    </summary>

                    <div className="mt-3 space-y-1 font-mono text-[11px] leading-5">
                      {datasetId ? <div>datasetId: {String(datasetId)}</div> : null}
                      {taxYear ? <div>year: {String(taxYear)}</div> : null}
                      {currentStep ? <div>step: {String(currentStep)}</div> : null}
                      {errorCode ? <div>code: {String(errorCode)}</div> : null}
                      {errorDetail ? (
                        <div className="break-words">
                          detail: {String(errorDetail).slice(0, 800)}
                        </div>
                      ) : null}
                    </div>
                  </details>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes overlay-fade-in {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes overlay-in {
          0% {
            opacity: 0;
            transform: translateY(8px) scale(0.992);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes overlay-sheen {
          0% {
            transform: translateX(-130%);
          }
          100% {
            transform: translateX(130%);
          }
        }

        @keyframes overlay-indeterminate {
          0% {
            transform: translateX(-120%);
          }
          100% {
            transform: translateX(320%);
          }
        }

        .animate-overlay-fade-in {
          animation: overlay-fade-in 0.30s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .animate-overlay-in {
          animation: overlay-in 0.44s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .overlay-logo-sheen {
          background: linear-gradient(
            120deg,
            transparent 0%,
            rgba(255,255,255,0.10) 30%,
            rgba(255,255,255,0.34) 50%,
            rgba(255,255,255,0.10) 70%,
            transparent 100%
          );
          transform: translateX(-130%);
          animation: overlay-sheen 2.8s ease-in-out infinite;
        }

        .overlay-progress-sheen {
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.18) 45%,
            rgba(255,255,255,0.34) 50%,
            rgba(255,255,255,0.18) 55%,
            transparent 100%
          );
          transform: translateX(-130%);
          animation: overlay-sheen 2s ease-in-out infinite;
        }

        .overlay-indeterminate {
          animation: overlay-indeterminate 1.15s ease-in-out infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-overlay-fade-in,
          .animate-overlay-in,
          .overlay-logo-sheen,
          .overlay-progress-sheen,
          .overlay-indeterminate {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
